import { useState } from "react";
import { Loader2, X, Search } from "lucide-react";
import { useResponsive } from "@design-lab/shared/hooks/useResponsive";
import { Button } from "@design-lab/shared/ui/design-system/Button";
import { Input } from "@design-lab/shared/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@design-lab/shared/ui/select";

export type SortOption = {
  value: string;
  label: string;
};

type SearchBarProps = {
  searchValue: string;
  onSearchChange: (value: string) => void;
  sortValue: string;
  onSortChange: (value: string) => void;
  sortOptions: SortOption[];
  searchPlaceholder?: string;
  isSearchPending?: boolean;
  searchTestId?: string;
  sortTestId?: string;
  clearSearchTestId?: string;
  expandSearchTestId?: string;
  closeSearchTestId?: string;
};

export function SearchBar({
  searchValue,
  onSearchChange,
  sortValue,
  onSortChange,
  sortOptions,
  searchPlaceholder = "Search image names",
  isSearchPending = false,
  searchTestId = "search-input",
  sortTestId = "sort-select-trigger",
  clearSearchTestId = "clear-search",
  expandSearchTestId = "expand-search-button",
  closeSearchTestId = "close-search-button"
}: SearchBarProps) {
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const { isDesktop } = useResponsive();

  const handleSearchExpand = () => setIsSearchExpanded(true);
  const handleSearchCollapse = () => {
    setIsSearchExpanded(false);
    onSearchChange("");
  };

  // Desktop view
  if (isDesktop) {
    return (
      <div className="flex-shrink-0">
        <div className="m-1 mb-2 flex items-center gap-4">
          <Select value={sortValue} onValueChange={onSortChange}>
            <SelectTrigger data-testid={sortTestId} className="w-[180px]">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              {sortOptions.map(option => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="relative ml-auto w-[200px]">
            <Input
              data-testid={searchTestId}
              placeholder={searchPlaceholder}
              className="w-full pr-8"
              value={searchValue}
              onChange={e => onSearchChange(e.target.value)}
            />
            {searchValue && !isSearchPending && (
              <button
                data-testid={clearSearchTestId}
                onClick={() => onSearchChange("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                aria-label="Clear search"
              >
                <X className="size-4" />
              </button>
            )}
            {isSearchPending && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                <Loader2 className="size-3 animate-spin text-gray-400" />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Mobile view
  return (
    <div className="flex-shrink-0">
      <div className="m-1 mb-2 flex items-center gap-4">
        {isSearchExpanded ? (
          // Mobile expanded search: full-width input with close button
          <div className="flex w-full items-center gap-2">
            <Input
              data-testid={searchTestId}
              placeholder={searchPlaceholder}
              className="flex-1"
              value={searchValue}
              onChange={e => onSearchChange(e.target.value)}
              autoFocus
            />
            <Button
              data-testid={closeSearchTestId}
              variant="outline"
              size="icon"
              onPress={handleSearchCollapse}
              className="flex-shrink-0"
            >
              <X className="size-4" />
            </Button>
          </div>
        ) : (
          // Default layout: sort dropdown + search icon
          <>
            <Select value={sortValue} onValueChange={onSortChange}>
              <SelectTrigger data-testid={sortTestId} className="w-[180px]">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                {sortOptions.map(option => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              data-testid={expandSearchTestId}
              variant="outline"
              size="icon"
              onPress={handleSearchExpand}
              className="ml-auto flex-shrink-0"
              aria-label="Search"
            >
              <Search className="size-4" />
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
